"use client";

import { useContext } from "react";
import { XIcon } from "lucide-react";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { SuperLink } from "@/components/common/super-link";
import { Button } from "@/components/ui/button";
import { createPath } from "@/service/routing/routing";
import { cn } from "@/lib/utils";

const DOCTORS_IMAGE = "/home/ekg-banner/doctors.webp";
const REMEDIUM_LOGO = "/home/ekg-banner/remedium-logo.svg";
const ZENTIVA_LOGO = "/home/ekg-banner/zentiva-logo.webp";
const PLAY_ICON = "/home/ekg-banner/play-icon.svg";

const TITLE = "EKG w przypadkach";
const SUBTITLE =
  "– od interpretacji zapisu do decyzji o leczeniu przeciwkrzepliwym";
const KICKER = "Nowy kurs w Remedium";
const CTA_HEADLINE = "Sprawdź pierwsze 3 odcinki!";
const CTA_SUBLINE = "Kolejne dostępne już 1 listopada";
const CTA_LABEL = "Odblokuj kurs bezpłatnie";

/*
 * Tło z Figmy (frame 1212×200). Radialny gradient z makiety ma promień pionowy ~5300 px przy
 * 200 px wysokości baneru, więc degeneruje się do poziomego — stąd `to left` zamiast `radial-gradient`.
 * Na wierzchu granatowa nakładka, w makiecie obrócony gradient liniowy biegnący w lewo.
 */
const DESKTOP_BACKGROUND = [
  "linear-gradient(to right, rgba(4,10,62,0.83) 0%, rgba(4,11,71,0.5) 28.79%, rgba(5,13,84,0) 57.81%)",
  "linear-gradient(to left, #FE6165 0%, #FF3A6F 23.6%, #FF2674 35.4%, #FF1279 47.19%)",
].join(",");

/*
 * Makieta nie zawiera wariantu mobilnego — układ jest złożony na nowo z tych samych elementów:
 * różowy pas ze zdjęciami u góry przechodzi w granat, na którym siedzi treść.
 * Stopy w px (nie %), bo wysokość karty zależy od zawijania tekstu.
 */
const MOBILE_BACKGROUND =
  "linear-gradient(to bottom, #FF3A6F 0px, #FF1279 80px, #04093A 150px)";

/* Ikona z Figmy ma wypalony cień, więc plik jest większy niż samo kółko — inset odtwarza tę nadwyżkę. */
const PlayIcon = ({ className }: { className?: string }) => (
  <span className={cn("relative block shrink-0", className)}>
    <img
      src={PLAY_ICON}
      alt=""
      aria-hidden
      className="absolute top-[-22.18%] left-[-27.31%] w-[154.61%] max-w-none"
    />
  </span>
);

export const HomeEkgBanner = ({
  closeBanner,
}: {
  closeBanner?: (() => void) | null;
}) => {
  const { logView } = useContext(ResourceContext);

  const handleLinkClick = () => {
    logEvent("home_banner_button_click", logView, {
      banner_type: "ekg",
    });
  };

  return (
    <div className="group @container relative mb-4 w-full">
      <SuperLink
        href={createPath("course.show", {
          slug: "ekg-w-przypadkach",
          id: 19,
        })}
        onClick={handleLinkClick}
        aria-label={`Przejdź do kursu ${TITLE}`}
        className="absolute inset-0 z-10"
      />

      {closeBanner && (
        <Button
          variant="neutral"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeBanner();
          }}
          aria-label="Zamknij baner"
          className="absolute top-2 right-2 z-20 size-9 text-white lg:top-[0.7574cqw] lg:right-[1.2376cqw] lg:size-[3.3003cqw] [&_path]:stroke-white!"
        >
          <XIcon className="size-5! lg:size-[1.6502cqw]!" />
        </Button>
      )}

      {/* Mobile / tablet — układ pionowy */}
      <div
        className="relative overflow-hidden rounded-2xl lg:hidden"
        style={{ backgroundImage: MOBILE_BACKGROUND }}
      >
        <div className="relative h-[150px] overflow-hidden">
          <img
            src={DOCTORS_IMAGE}
            alt=""
            aria-hidden
            className="absolute top-0 left-1/2 h-[300px] w-[338px] max-w-none -translate-x-1/2"
          />
          <div className="absolute inset-x-0 bottom-0 h-[70px] bg-gradient-to-b from-transparent to-[#04093A]" />
        </div>

        {/* Kolumna treści nie rozjeżdża się na tablecie — poniżej `lg` baner potrafi mieć ~1000 px. */}
        <div className="mx-auto max-w-[460px] px-5 pb-6 text-center">
          <p className="text-xs leading-[1.24] font-bold tracking-[-0.24px] text-[#0EF2BB] uppercase">
            {KICKER}
          </p>
          <h2 className="mt-2 text-[28px] leading-[1.05] font-bold tracking-[-1.4px] text-white">
            {TITLE}
          </h2>
          <p className="mt-2 text-sm leading-[1.23] tracking-[-0.28px] text-white">
            {SUBTITLE}
          </p>

          <p className="mt-5 text-base leading-[1.23] font-bold tracking-[-0.32px] text-white">
            {CTA_HEADLINE}
          </p>
          <p className="text-sm leading-[1.23] tracking-[-0.28px] text-white">
            {CTA_SUBLINE}
          </p>

          <Button
            asChild
            variant="white"
            className="mt-4 h-12 w-full gap-2 rounded-full py-0 pr-2 pl-4 text-sm font-semibold tracking-[-0.42px] text-[#02083C] uppercase transition-transform group-hover:scale-[1.02]"
          >
            <span>
              {CTA_LABEL}
              <PlayIcon className="size-7" />
            </span>
          </Button>

          <div className="mt-5 flex items-start justify-center gap-[26px]">
            <img
              src={REMEDIUM_LOGO}
              alt="Remedium"
              className="h-[18px] w-[104px]"
            />
            <img
              src={ZENTIVA_LOGO}
              alt="Zentiva"
              className="mt-[7px] h-[13px] w-[73px] brightness-0 invert"
            />
          </div>
        </div>
      </div>

      {/*
        Desktop — kalka z Figmy 1212×200. Wszystkie wymiary w `cqw` (1 cqw = 12.12 px makiety),
        więc baner skaluje się proporcjonalnie na każdej szerokości kolumny treści.
      */}
      <div
        className="relative hidden aspect-[1212/200] w-full overflow-hidden rounded-[1.9802cqw] lg:block"
        style={{ backgroundImage: DESKTOP_BACKGROUND }}
      >
        <img
          src={DOCTORS_IMAGE}
          alt=""
          aria-hidden
          className="absolute top-[0.9901cqw] left-[66.1703cqw] h-[26.0713cqw] w-[29.3979cqw] max-w-none"
        />

        <p className="absolute top-[3.4518cqw] left-[2.9389cqw] -translate-y-1/2 text-[1.3201cqw] leading-[1.24] font-bold tracking-[-0.0264cqw] whitespace-nowrap text-[#0EF2BB] uppercase">
          {KICKER}
        </p>

        <div className="absolute top-[6.1109cqw] left-[2.9389cqw] flex w-[32.1334cqw] flex-col gap-[1.4796cqw] text-white">
          <h2 className="text-[3.3003cqw] leading-[2.654cqw] font-bold tracking-[-0.165cqw] [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
            {TITLE}
          </h2>
          <p className="text-[1.4439cqw] leading-[1.23] tracking-[-0.0289cqw] [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
            {SUBTITLE}
          </p>
        </div>

        <div className="absolute top-[1.8498cqw] left-1/2 flex w-[23.5149cqw] -translate-x-1/2 flex-col items-center gap-[0.165cqw] text-center text-white">
          <p className="text-[1.7327cqw] leading-[1.23] font-bold tracking-[-0.0347cqw]">
            {CTA_HEADLINE}
          </p>
          <p className="text-[1.4915cqw] leading-[1.23] tracking-[-0.0298cqw]">
            {CTA_SUBLINE}
          </p>
        </div>

        <Button
          asChild
          variant="white"
          className="absolute top-[7.4604cqw] left-1/2 h-[3.9604cqw] -translate-x-1/2 gap-[0.7278cqw] rounded-[3.0325cqw] py-0 pr-[0.7278cqw] pl-[1.213cqw] text-[1.3201cqw] leading-none font-semibold tracking-[-0.0396cqw] text-[#02083C] uppercase transition-transform group-hover:scale-[1.02]"
        >
          <span>
            {CTA_LABEL}
            <PlayIcon className="size-[2.3654cqw]" />
          </span>
        </Button>

        <div className="absolute top-[12.906cqw] left-1/2 flex -translate-x-1/2 items-start gap-[2.5776cqw]">
          <img
            src={REMEDIUM_LOGO}
            alt="Remedium"
            className="h-[1.5059cqw] w-[8.6721cqw]"
          />
          <img
            src={ZENTIVA_LOGO}
            alt="Zentiva"
            className="mt-[0.5786cqw] h-[1.1075cqw] w-[6.11cqw] brightness-0 invert"
          />
        </div>
      </div>
    </div>
  );
};
